import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck } from "lucide-react";

const apiErr = (e) => e?.response?.data?.detail || "Something went wrong";

export default function PlatformAdmins() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/platform/admins").then((r) => setAdmins(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { if (user?.is_super_admin) load(); }, [user]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 8) return toast.error("Name, email and an 8+ character password are required");
    setBusy(true);
    try {
      await api.post("/platform/admins", form);
      toast.success("Admin created — share their email & password securely");
      setForm({ name: "", email: "", password: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove admin ${a.email}? They'll lose platform access immediately.`)) return;
    try { await api.delete(`/platform/admins/${a.id}`); toast.success("Admin removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (user && !user.is_super_admin) return <Navigate to="/platform" replace />;

  return (
    <div data-testid="platform-admins">
      <h1 className="font-heading font-extrabold text-2xl">Admins</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-6 max-w-2xl">Give trusted people full platform access — they can manage clubs, revenue and content. Only you (the super owner) can add or remove admins.</p>

      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 mb-8 max-w-xl" data-testid="add-admin-card">
        <p className="font-heading font-semibold mb-4 flex items-center gap-2"><UserPlus size={18} className="text-primary" /> Add an admin</p>
        <div className="space-y-4">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" className="mt-1.5" data-testid="admin-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@example.com" className="mt-1.5" data-testid="admin-email" /></div>
          <div><Label>Temporary password</Label><Input value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 8 characters" className="mt-1.5" data-testid="admin-password" /><p className="text-xs text-muted-foreground mt-1">Share this with them securely — they can change it later in Settings.</p></div>
          <Button onClick={create} disabled={busy} className="brand-gradient text-white border-0" data-testid="admin-create-btn">{busy ? "Creating…" : "Create admin"}</Button>
        </div>
      </div>

      <div className="space-y-2.5" data-testid="admin-list">
        {admins.map((a) => (
          <div key={a.id} className="flex items-center gap-4 rounded-xl border border-border bg-card p-4" data-testid={`admin-row-${a.id}`}>
            <div className="w-10 h-10 rounded-full brand-gradient flex items-center justify-center text-white font-bold shrink-0">{(a.name || a.email)[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{a.name || "—"} {a.is_super_admin && <span className="ml-1 text-[10px] uppercase tracking-wider text-secondary font-semibold inline-flex items-center gap-1"><ShieldCheck size={11} /> Super owner</span>}</p>
              <p className="text-xs text-muted-foreground truncate">{a.email}</p>
            </div>
            {!a.is_super_admin && a.id !== user?.id && (
              <Button size="icon" variant="ghost" onClick={() => remove(a)} className="text-destructive hover:text-destructive" data-testid={`admin-remove-${a.id}`}><Trash2 size={16} /></Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
